import React, { useEffect, useState } from "react";
import { jobsApi, candidatesApi, aiApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AICopilotPage() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState("");

  useEffect(() => {
    jobsApi.list().then((j) => { setJobs(j); if (j[0]) setSelected(j[0].id); });
  }, []);

  const rank = async () => {
    if (!selected) return;
    setRunning(true); setResult("");
    try {
      const cands = await candidatesApi.list();
      const targetJob = jobs.find((j) => j.id === selected);
      const matches = cands.filter((c) => c.title === targetJob?.title).slice(0, 8);
      const res = await aiApi.rank(selected, matches.map((c) => c.id));
      setResult(res.result);
      toast.success("Ranking complete — review before actioning");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI error");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#4338CA] mb-1 font-bold">AI Copilot</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Rank Candidates</h1>
        <p className="text-muted-foreground text-sm mt-1">Powered by Claude Sonnet 4.6 · Explainable · Human sign-off required</p>
      </div>

      <Card className="border-border shadow-none p-6">
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-end">
          <div className="flex-1 w-full">
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Select job</label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} · {j.client}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={rank} disabled={running || !selected} className="bg-[#4338CA] hover:bg-[#3730A3] h-11 gap-2 min-w-40">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {running ? "Ranking…" : "Rank matched candidates"}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="border-border shadow-none overflow-hidden">
          <div className="p-6 border-b border-border flex items-center gap-3 bg-gradient-to-r from-white to-indigo-50/40">
            <div className="h-8 w-8 rounded-md bg-[#4338CA] text-white grid place-items-center"><Sparkles className="h-4 w-4" /></div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight">AI Recommendation</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-mono text-[10px] tracking-wider">Advisory · Not a hiring decision</Badge>
              </div>
            </div>
          </div>
          <div className="p-6">
            <pre className="whitespace-pre-wrap font-mono text-sm bg-[#FAFAFA] border border-border rounded-md p-4">{result}</pre>
          </div>
        </Card>
      )}
    </div>
  );
}
